import { useSessionStorage } from '@healthhub/react-ui';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { ConnectionError, ConnectionErrorMobile } from '@shared/assets';
import { Button } from '@shared/components/Button';
import { CONNECTION_ERROR_BASE64_SESSION_KEY } from '@shared/constants/constants';
import { useLayoutViewContext } from '@shared/context';
import { TRY_AGAIN_ERROR_MESSAGE } from '@shared/services/errorMessage';

const { Link, Text } = Typography;

export interface NoInternetScreenProps {
  buttonName?: string;
  errorMsg?: string;
  hideEmail?: boolean;
  hideTryAgainMsg?: boolean;
  isLoading?: boolean;
  onRetry?: () => void;
  title?: string;
}

const NoInternetScreen = ({
  buttonName = 'Try Again',
  errorMsg = 'Please check your network connections and try again.',
  hideEmail,
  hideTryAgainMsg,
  onRetry,
  title = 'Oops something went wrong.',
  isLoading,
}: NoInternetScreenProps) => {
  const { isMobileView } = useLayoutViewContext();

  const { t } = useTranslation();
  const [storedValue] = useSessionStorage<string>(CONNECTION_ERROR_BASE64_SESSION_KEY);
  const connectionErrorSVG = `data:image/svg+xml;base64,${storedValue}`;

  const getErrorImage = () => {
    if (storedValue) {
      return connectionErrorSVG;
    }
    if (isMobileView) {
      return ConnectionErrorMobile;
    }
    return ConnectionError;
  };

  return (
    <div className="flex flex-col justify-center px-4 pt-24 text-center">
      <div className="flex flex-col items-center gap-y-5">
        <img alt="" data-testid="no-internet-screen-image" loading="eager" src={getErrorImage()} />
        <Text className="text-sm inline-block font-semibold">{title}</Text>
        <div className="flex flex-col">
          <Text className="text-sm">{errorMsg}</Text>
          {!hideTryAgainMsg && <Text className="text-sm">{TRY_AGAIN_ERROR_MESSAGE}</Text>}
        </div>
        {!hideEmail && (
          <Text className="text-sm text-center">
            {t('emptyState.contactUs')}&nbsp;
            <Link className="email-link font-semibold text-theme-600" href="mailto:contact_us@healthhub.sg">
              contact_us@healthhub.sg.
            </Link>
          </Text>
        )}
      </div>
      {!!onRetry && (
        <Button
          className="mt-8 flex h-12 w-full items-center justify-center self-center rounded-md bg-theme-700 p-4 text-white"
          loading={isLoading}
          onClick={onRetry}
        >
          {buttonName}
        </Button>
      )}
    </div>
  );
};

export default NoInternetScreen;
